import { useReadQuery, type QueryRef } from '@apollo/client/react';

import { Skeleton } from '@/components/ui/skeleton';
import { BillingCard } from 'src/components/Billing';
import { withSuspense } from 'src/hoc';
import { Table, TableRow, TableBody, TableCell } from '@/components/ui/table';
import { billingCountriesDictionary } from 'src/shared/entities';

import type { IBillingQuery } from 'src/graphql/actions/billing/billing.query';

import { EditDetailsDialog } from './ui';

type Props = {
  queryRef: QueryRef<IBillingQuery>;
};

const BillingInformationContainer = ({ queryRef }: Props) => {
  const {
    data: { billing },
  } = useReadQuery(queryRef);

  const billingContact =
    [billing?.billingAddress?.firstName, billing?.billingAddress?.lastName].filter(Boolean).join(' ') || '-';
  const fullAddress = [
    billing?.billingAddress?.addressLine1,
    billing?.billingAddress?.city,
    billing?.billingAddress?.zip,
    billing?.billingAddress?.city,
    billingCountriesDictionary[billing?.billingAddress?.country || ''],
  ]
    .filter((value) => value)
    .join(', ');
  const fullVatNumber = billing?.billingAddress?.companyRegistrationNumber
    ? `${billing?.billingAddress?.country}${billing?.billingAddress?.companyRegistrationNumber}`
    : '-';

  return (
    <BillingCard
      title="Billing details"
      description="View and update your organization’s billing details here."
      className="mb-4"
      action={billing?.policy.update && <EditDetailsDialog data={billing?.billingAddress} />}
    >
      <Table>
        <TableBody>
          <TableRow>
            <TableCell className="w-[190px] font-semibold">Billing contact</TableCell>
            <TableCell className="w-[85px]">:</TableCell>
            <TableCell>{billingContact}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="w-[190px] font-semibold">Email</TableCell>
            <TableCell className="w-[85px]">:</TableCell>
            <TableCell>{billing?.billingAddress?.email || '-'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="w-[190px] font-semibold">Address</TableCell>
            <TableCell className="w-[85px]">:</TableCell>
            <TableCell>{fullAddress || '-'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="w-[190px] font-semibold">VAT no.</TableCell>
            <TableCell className="w-[85px]">:</TableCell>
            <TableCell>{fullVatNumber}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </BillingCard>
  );
};

const InformationSkeleton = () => (
  <BillingCard
    title="Billing details"
    description="View and update your organization’s billing details here."
    className="mb-4"
  >
    <div className="flex flex-col gap-3">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
    </div>
  </BillingCard>
);

export default withSuspense(BillingInformationContainer, InformationSkeleton);
